import React, { useContext } from 'react';
import {
  makeStyles,
  Menu,
  MenuDivider,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  shorthands,
  tokens,
  ToolbarDivider,
} from '@fluentui/react-components';
import {
  ArrowRedo20Regular,
  ArrowUndo20Regular,
  TextAlignCenter24Regular,
  TextAlignJustify24Regular,
  TextAlignLeft20Regular,
  TextAlignLeft24Regular,
  TextAlignRight24Regular,
  TextBold20Regular,
  TextItalic20Regular,
  TextStrikethrough20Regular,
  TextSubscriptRegular,
  TextSuperscriptRegular,
  TextUnderline20Regular,
  TextboxAlignBottom20Regular,
  TextboxAlignMiddle20Regular,
  TextboxAlignTop20Regular,
} from '@fluentui/react-icons';

import ToolbarToggleButton from './toolbar/ToolbarToggleButton';
import ToolbarButton from './toolbar/ToolbarButton';
import ToolbarMenuButton from './toolbar/ToolbarMenuButton';
import ToolbarMenuItem from './toolbar/ToolbarMenuItem';
import TextStyleDropdown from './toolbar/TextStyleDropdown';

import DocumentContext from '../../contexts/DocumentContext.context';
import useEditorActive from '../../hooks/useEditorActive.hook';
import { usesFormFields } from '../../helpers/formFields.helper';
import AddFieldMenu from '../formFields/AddFieldMenu';
import { SignInContext } from '../../../users';
import OrderedListButton from './toolbar/custom/OrderedListButton';
import BulletListButton from './toolbar/custom/BulletListButton';
import InvisibleCharactersButton from './toolbar/custom/InvisibleCharactersButton';
import { ELEMENT_ROW_NODE_NAME } from '../../tiptap/nodes/elementRow/constants';
import { CAPTION_NODE_NAME } from '../../tiptap/nodes/caption/constants';
import { canAlignHorizontally, getHorizontalAlignmentState, isInCell, isInList } from './alignmentState';
import { getVerticalAlignmentState, setVerticalAlignment } from '../../tiptap/nodes/table/cellAlignmentCommands';
import { ETableCellVerticalAlignment } from '../../tiptap/nodes/table/cellAttributes';
import { ETiptapAlignmentSelectorKey, ETiptapSelectorKey } from '../../types/tiptap.types';
import { IMAGE_NODE_NAME } from '../../tiptap/nodes/image/constants';

const useStyles = makeStyles({
  menu: {
    padding: '0',
  },
  menuButton: {
    alignItems: 'flex-start',
    '& .fui-MenuButton__menuIcon': {
      fontSize: '15px',
    },
    fontWeight: 'normal',
    width: '155px',
    marginRight: tokens.spacingVerticalS,
  },
  // Add field looks like Normal text beside it, at the width of its own label.
  addFieldButton: {
    width: 'auto',
  },
});

const ALIGNMENTS = {
  [ETiptapAlignmentSelectorKey.LEFT]: {
    icon: <TextAlignLeft24Regular className="text-neutralFg-2 -mt-1 -mb-0.5" />,
    menuIcon: <TextAlignLeft20Regular />,
    label: 'Align Left',
  },
  [ETiptapAlignmentSelectorKey.CENTER]: {
    icon: <TextAlignCenter24Regular className="text-neutralFg-2 -mt-1 -mb-0.5" />,
    menuIcon: <TextAlignCenter24Regular />,
    label: 'Center',
  },
  [ETiptapAlignmentSelectorKey.RIGHT]: {
    icon: <TextAlignRight24Regular className="text-neutralFg-2 -mt-1 -mb-0.5" />,
    menuIcon: <TextAlignRight24Regular />,
    label: 'Align Right',
  },
  [ETiptapAlignmentSelectorKey.JUSTIFY]: {
    icon: <TextAlignJustify24Regular className="text-neutralFg-2 -mt-1 -mb-0.5" />,
    menuIcon: <TextAlignJustify24Regular />,
    label: 'Justify',
  },
};

const VERTICAL_ALIGNMENTS = {
  [ETableCellVerticalAlignment.TOP]: {
    menuIcon: <TextboxAlignTop20Regular />,
    label: 'Align Top',
  },
  [ETableCellVerticalAlignment.CENTER]: {
    menuIcon: <TextboxAlignMiddle20Regular />,
    label: 'Align Center Vertically',
  },
  [ETableCellVerticalAlignment.BOTTOM]: {
    menuIcon: <TextboxAlignBottom20Regular />,
    label: 'Align Bottom',
  },
};

// This component is memoized and re-renders only when `useEditorToolbar` returns a new object or when another item (document/file) is selected.
const Home = React.memo(() => {
  const { doc, editor, isEdit } = useContext(DocumentContext);
  const { organization } = useContext(SignInContext);

  const isElementRowActive = useEditorActive((editor) => editor.isActive(ELEMENT_ROW_NODE_NAME));
  const isImageActive = useEditorActive((editor) => editor.isActive(IMAGE_NODE_NAME));
  const isCaptionActive = useEditorActive((editor) => editor.isActive(CAPTION_NODE_NAME));

  const isTableCellActive = useEditorActive(isInCell);
  const isListActive = useEditorActive(isInList);

  const currentAlignment = useEditorActive(getHorizontalAlignmentState);
  // Own subscription: currentAlignment often does not change when the caret enters a heading,
  // so a render-time expression would go stale (useEditorActive re-renders only on value change).
  const canAlignHorizontal = useEditorActive(canAlignHorizontally);
  const currentVerticalAlignment = useEditorActive(getVerticalAlignmentState);
  const canAlignVertical = isTableCellActive;

  const setAlign = (textAlign: ETiptapAlignmentSelectorKey) => {
    const chain = editor.chain().focus();

    if (isElementRowActive) {
      chain.updateAttributes(ELEMENT_ROW_NODE_NAME, { textAlign });
    } else if (isImageActive) {
      chain.updateAttributes(IMAGE_NODE_NAME, { textAlign }).run();
      return;
    } else if (isCaptionActive) {
      chain.updateAttributes(CAPTION_NODE_NAME, { textAlign });
    }

    // In table cells: lists use cell-level data-align (CSS handles block positioning)
    // This prevents marker/text split and aligns the entire list as a block
    if (isTableCellActive && isListActive) {
      const alignValue = textAlign === ETiptapAlignmentSelectorKey.LEFT ? undefined : textAlign;
      chain.setCellAttribute('align', alignValue).run();
      return;
    }

    chain.setTextAlign(textAlign).run();
  };

  const classes = useStyles();
  return (
    <>
      {/* The history holds the content load until editing clears it, so read-only it would still offer undo. */}
      <ToolbarButton
        icon={<ArrowUndo20Regular />}
        tooltip="Undo"
        worksWhenLocked
        disabled={(editor) => !isEdit || !editor.can().chain().focus().undo().run()}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolbarButton
        icon={<ArrowRedo20Regular />}
        tooltip="Redo"
        worksWhenLocked
        disabled={(editor) => !isEdit || !editor.can().chain().focus().redo().run()}
        onClick={() => editor.chain().focus().redo().run()}
      />
      <ToolbarDivider />
      {usesFormFields(doc) && <AddFieldMenu className={mergeClasses(classes.menuButton, classes.addFieldButton)} />}
      {!doc.isBlock && <TextStyleDropdown editor={editor} org={organization} classes={classes} />}
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.BOLD}
        icon={<TextBold20Regular />}
        tooltip="Bold"
        disabled={(editor) => !editor.can().chain().focus().toggleBold().run()}
        onClick={() => editor.chain().focus().toggleBold().run()}
        className="ml-2"
      />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.ITALIC}
        icon={<TextItalic20Regular />}
        tooltip="Italic"
        disabled={(editor) => !editor.can().chain().focus().toggleItalic().run()}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.UNDERLINE}
        icon={<TextUnderline20Regular />}
        tooltip="Underline"
        disabled={(editor) => !editor.can().chain().focus().toggleUnderline().run()}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.STRIKE}
        icon={<TextStrikethrough20Regular />}
        tooltip="StrikeThrough"
        disabled={(editor) => !editor.can().chain().focus().toggleStrike().run()}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.SUBSCRIPT}
        icon={<TextSubscriptRegular />}
        tooltip="Subscript"
        disabled={(editor) => !editor.can().chain().focus().unsetSuperscript().toggleSubscript().run()}
        onClick={() => editor.chain().focus().unsetSuperscript().toggleSubscript().run()}
      />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.SUPERSCRIPT}
        icon={<TextSuperscriptRegular />}
        tooltip="Superscript"
        disabled={(editor) => !editor.can().chain().focus().unsetSubscript().toggleSuperscript().run()}
        onClick={() => editor.chain().focus().unsetSubscript().toggleSuperscript().run()}
      />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.HIGHLIGHT}
        icon={
          <svg height="100%" width="100%" viewBox="0,0,2048,2048" focusable="false">
            <path
              type="path"
              style={{ fill: 'transparent' }}
              d="M 2048 2048 h -2048 v -307 h 2048 m -177 -1492 q 38 38 57 85 q 18 47 18 97 q 0 49 -18 96 q -19 47 -57 85 l -905 905 l -249 19 v 102 h -615 l 308 -204 l 51 -280 l 71 -72 h -1 l 905 -905 q 38 -38 85 -56 q 47 -19 96 -19 q 50 0 97 19 q 47 18 85 56 m 0 362 q 21 -21 33 -49 q 11 -29 11 -60 q -1 -30 -12 -58 q -11 -28 -32 -50 l -73 -73 q -21 -21 -49 -32 q -29 -12 -59 -12 q -32 1 -60 12 q -28 11 -49 32 l -833 833 l 290 290 z"
            ></path>
            <path type="path" style={{ fill: '#FFFFFFFF' }} d="M 31 2017 v -245 h 1986 v 245 z"></path>
            <path
              type="path"
              style={{ fill: '#C8C6C4FF' }}
              d="M 2048 1741 v 307 h -2048 v -307 m 1986 62 h -1924 v 183 h 1924 z"
            ></path>
            <path type="path" style={{ fill: 'rgb(255, 255, 0)' }} d="M 2048 2048 h -2048 v -307 h 2048 z"></path>
            <path
              type="path"
              style={{ fill: '#797774FF' }}
              d="M 102 1638 l 308 -204 l 51 -280 l 129 -130 l 376 493 l -249 19 v 102 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#FAFAFAFF' }}
              d="M 604 1082 l 869 -869 q 29 -29 66 -44 q 37 -15 79 -15 q 41 0 78 15 q 37 15 66 44 l 73 73 q 29 29 44 66 q 15 37 15 78 q 0 42 -15 79 q -15 37 -44 66 l -869 869 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#3A3A38FF' }}
              d="M 1871 249 q 38 38 57 85 q 18 47 18 97 q 0 49 -18 96 q -19 47 -57 85 l -905 905 l -435 -435 l 905 -905 q 38 -38 85 -56 q 47 -19 96 -19 q 50 0 97 19 q 47 18 85 56 m 0 362 q 21 -21 33 -49 q 11 -29 11 -60 q -1 -30 -12 -58 q -11 -28 -32 -50 l -73 -73 q -21 -21 -49 -32 q -29 -12 -59 -12 q -32 1 -60 12 q -28 11 -49 32 l -833 833 l 290 290 z"
            ></path>
          </svg>
        }
        tooltip="Highlight"
        disabled={(editor) =>
          editor.isActive(ETiptapSelectorKey.HEADING) || !editor.can().chain().focus().toggleHighlight().run()
        }
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      />
      <ToolbarButton
        icon={
          <svg height="100%" width="100%" viewBox="0,0,2048,2048" focusable="false">
            <path
              type="path"
              style={{ fill: 'transparent' }}
              d="M 1036 1145 h -635 l -145 391 h -147 l 545 -1434 h 134 l 383 1012 l -106 107 m -74 -197 l -245 -671 q -10 -26 -16 -52 q -6 -25 -11 -49 h -4 q -5 26 -12 51 q -7 25 -16 50 l -241 671 m 1602 300 l -724 724 l -435 -434 l 725 -725 m 0 145 l -356 356 l 289 290 l 356 -356 m -428 428 l -290 -290 l -151 152 l 290 289 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#3A3A38FF' }}
              d="M 1036 1145 h -635 l -145 391 h -147 l 545 -1434 h 134 l 383 1012 l -106 107 m -74 -197 l -245 -671 q -10 -26 -16 -52 q -6 -25 -11 -49 h -4 q -5 26 -12 51 q -7 25 -16 50 l -241 671 z"
            ></path>
            <path type="path" style={{ fill: '#FAFAFAFF' }} d="M 1184 1391 l 430 -429 l 362 362 l -430 429 z"></path>
            <path type="path" style={{ fill: '#719C85' }} d="M 962 1614 l 222 -223 l 362 362 l -222 223 z"></path>
            <path
              type="path"
              style={{ fill: '#254937' }}
              d="M 2048 1324 l -724 724 l -435 -434 l 725 -725 m 0 145 l -356 356 l 289 290 l 356 -356 m -428 428 l -290 -290 l -151 152 l 290 289 z"
            ></path>
          </svg>
        }
        tooltip="Clear Formatting"
        // Headings allow no marks, so there is never anything to clear there - but unsetAllMarks
        // reports success for the no-op, so can() alone would keep the button enabled.
        disabled={(editor) =>
          editor.isActive(ETiptapSelectorKey.HEADING) || !editor.can().chain().focus().unsetAllMarks().run()
        }
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
      />
      {/*onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()*/}
      <ToolbarDivider />
      <BulletListButton editor={editor} menuClasses={classes} />
      <OrderedListButton editor={editor} menuClasses={classes} />
      <ToolbarToggleButton
        selector={ETiptapSelectorKey.TASK_LIST}
        icon={
          <svg height="100%" width="100%" viewBox="0,0,2048,2048" focusable="false">
            <path
              type="path"
              style={{ fill: 'transparent' }}
              d="M621 147q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m0 640q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m0 640q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m1235-1043h-1024q-27 0-45-19-19-19-19-45 0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19z m-1088 576q0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19h-1024q-27 0-45-19-19-19-19-45z m1088 576h-1024q-27 0-45 19-19 19-19 45 0 27 19 45 19 19 45 19h1024q27 0 45-19 19-19 19-45 0-27-19-45-19-19-45-19z"
            ></path>
            <path
              type="path"
              style={{ fill: '#C8C6C4FF' }}
              d="M621 147q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m0 640q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m0 640q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m1235-1043h-1024q-27 0-45-19-19-19-19-45 0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19z m-1088 576q0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19h-1024q-27 0-45-19-19-19-19-45z m1088 576h-1024q-27 0-45 19-19 19-19 45 0 27 19 45 19 19 45 19h1024q27 0 45-19 19-19 19-45 0-27-19-45-19-19-45-19z"
            ></path>
            <path
              type="path"
              style={{ fill: '#357457' }}
              d="M621 147q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m0 640q19 19 19 45 0 27-19 45l-256 256q-19 19-45 19-27 0-45-19l-128-128q-19-19-19-45 0-27 19-45 19-19 45-19 27 0 45 19l83 82 211-210q19-19 45-19 27 0 45 19z m0 730q19-19 19-45 0-27-19-45-19-19-45-19-27 0-45 19l-211 210-83-82q-19-19-45-19-27 0-45 19-19 19-19 45 0 27 19 45l128 128q19 19 45 19 27 0 45-19l256-256z"
            ></path>
            <path
              type="path"
              style={{ fill: '#3A3A38FF' }}
              d="M768 320q0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19h-1024q-27 0-45-19-19-19-19-45z m0 640q0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19h-1024q-27 0-45-19-19-19-19-45z m0 640q0-27 19-45 19-19 45-19h1024q27 0 45 19 19 19 19 45 0 27-19 45-19 19-45 19h-1024q-27 0-45-19-19-19-19-45z"
            ></path>
          </svg>
        }
        tooltip="Checklist"
        // toggleList falls back to clearNodes (converts the heading), so can() reports true there.
        disabled={(e) => e.isActive(ETiptapSelectorKey.HEADING) || !e.can().chain().focus().toggleTaskList().run()}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      />
      <ToolbarButton
        icon={
          <svg height="100%" width="100%" viewBox="0,0,2048,2048" focusable="false">
            <path
              type="path"
              style={{ fill: 'transparent' }}
              d="M 1946 205 h -1844 v -103 h 1844 m 0 1332 h -820 v -103 h 820 m 0 -717 h -820 v -102 h 820 m 0 512 h -820 v -102 h 820 m 0 921 h -1844 v -102 h 1844 m -1485 -1127 l 72 73 l -234 235 h 623 v 102 h -623 l 234 235 l -72 72 l -359 -358 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#3A3A38FF' }}
              d="M 1946 205 h -1844 v -103 h 1844 m 0 1332 h -820 v -103 h 820 m 0 -717 h -820 v -102 h 820 m 0 512 h -820 v -102 h 820 m 0 921 h -1844 v -102 h 1844 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#357457' }}
              d="M 461 614 l 72 73 l -234 235 h 623 v 102 h -623 l 234 235 l -72 72 l -359 -358 z"
            ></path>
          </svg>
        }
        tooltip="Decrease Indent"
        disabled={(e) => e.isActive(ETiptapSelectorKey.HEADING)}
        onClick={() => {
          if (
            editor.isActive(ETiptapSelectorKey.BULLET_LIST) ||
            editor.isActive(ETiptapSelectorKey.ORDERED_LIST_WITH_START) ||
            editor.isActive(ETiptapSelectorKey.TASK_LIST)
          )
            editor
              .chain()
              .focus()
              .liftListItem(
                editor.isActive(ETiptapSelectorKey.TASK_LIST)
                  ? ETiptapSelectorKey.TASK_ITEM
                  : ETiptapSelectorKey.LIST_ITEM
              )
              .run();
          else editor.chain().focus().outdent().run();
        }}
      />
      <ToolbarButton
        icon={
          <svg height="100%" width="100%" viewBox="0,0,2048,2048" focusable="false">
            <path
              type="path"
              style={{ fill: 'transparent' }}
              d="M 1946 205 h -1844 v -103 h 1844 m 0 1332 h -820 v -103 h 820 m 0 -717 h -820 v -102 h 820 m 0 512 h -820 v -102 h 820 m 0 921 h -1844 v -102 h 1844 m -1383 -410 l -72 -72 l 234 -235 h -623 v -102 h 623 l -234 -235 l 72 -73 l 359 359 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#3A3A38FF' }}
              d="M 1946 205 h -1844 v -103 h 1844 m 0 1332 h -820 v -103 h 820 m 0 -717 h -820 v -102 h 820 m 0 512 h -820 v -102 h 820 m 0 921 h -1844 v -102 h 1844 z"
            ></path>
            <path
              type="path"
              style={{ fill: '#357457' }}
              d="M 563 1331 l -72 -72 l 234 -235 h -623 v -102 h 623 l -234 -235 l 72 -73 l 359 359 z"
            ></path>
          </svg>
        }
        tooltip="Increase Indent"
        disabled={(e) => e.isActive(ETiptapSelectorKey.HEADING)}
        onClick={() => {
          if (
            editor.isActive(ETiptapSelectorKey.BULLET_LIST) ||
            editor.isActive(ETiptapSelectorKey.ORDERED_LIST_WITH_START) ||
            editor.isActive(ETiptapSelectorKey.TASK_LIST)
          )
            editor
              .chain()
              .focus()
              .sinkListItem(
                editor.isActive(ETiptapSelectorKey.TASK_LIST)
                  ? ETiptapSelectorKey.TASK_ITEM
                  : ETiptapSelectorKey.LIST_ITEM
              )
              .run();
          else editor.chain().focus().indent().run();
        }}
      />
      <Menu>
        <MenuTrigger>
          <ToolbarMenuButton
            icon={ALIGNMENTS[currentAlignment].icon}
            tooltip="Alignment"
            disabled={!canAlignHorizontal}
          />
        </MenuTrigger>

        <MenuPopover className={classes.menu}>
          <MenuList>
            {Object.entries(ALIGNMENTS).map(([key, value]) => (
              <ToolbarMenuItem
                key={key}
                isActive={currentAlignment === key}
                disabled={!canAlignHorizontal}
                icon={value.menuIcon}
                onClick={() => setAlign(key as ETiptapAlignmentSelectorKey)}
              >
                {value.label}
              </ToolbarMenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* Invisible characters toggle */}
      <InvisibleCharactersButton />
    </>
  );
});

export default Home;
